import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import * as boardApi from '../../api/board';
import type {
  NoticeListItem, NoticeDetail, PostListItem, PostDetail, PostCategory,
  FaqDto, InquiryDto,
} from '../../api/board';
import { BoardSidebar, type BoardSidebarItemId } from './BoardSidebar';
import { Combobox } from '../../components/shared';
// Cycle 44 — lang-aware date locale, Cycle 67 — tBoard i18n
import { formatDate, formatDateTime } from '../../lib/base-i18n';
import { tBoard, getBoardLang, subscribeBoardLang, type BoardI18nKey } from '../../lib/board-i18n';
import './BoardPage.css';

const POST_FILTER_OPTION_KEYS: ReadonlyArray<{ value: PostCategory | ''; labelKey: BoardI18nKey }> = [
  { value: '', labelKey: 'board.opt.all' },
  { value: 'free', labelKey: 'board.opt.free' },
  { value: 'tech', labelKey: 'board.opt.tech' },
  { value: 'info', labelKey: 'board.opt.info' },
];

const POST_DRAFT_OPTION_KEYS: ReadonlyArray<{ value: PostCategory; labelKey: BoardI18nKey }> = [
  { value: 'free', labelKey: 'board.opt.free' },
  { value: 'tech', labelKey: 'board.opt.tech' },
  { value: 'info', labelKey: 'board.opt.info' },
];

const INQUIRY_CATEGORY_OPTION_KEYS: ReadonlyArray<{ value: string; labelKey: BoardI18nKey }> = [
  { value: '계정', labelKey: 'board.inquiry.cat.account' },
  { value: '알림', labelKey: 'board.inquiry.cat.notification' },
  { value: '파일', labelKey: 'board.inquiry.cat.file' },
  { value: '보안', labelKey: 'board.inquiry.cat.security' },
  { value: '채팅', labelKey: 'board.inquiry.cat.chat' },
  { value: '연동', labelKey: 'board.inquiry.cat.integration' },
  { value: '기타', labelKey: 'board.inquiry.cat.other' },
];

/**
 * BoardPage — 시안 InfoView (init-docs/WorkChat.jsx line 2935~) 의
 * 4 group sidebar (공지/게시판/문의/FAQ) × 12 sub-item 정합.
 *
 * 사이드바 sub-item 변경 시 sub-tab component 의 `key` 가 바뀌어 unmount→remount —
 * initial state (importantOnly / category / creating) 가 props 로 적용된다.
 *
 * v1 deferred: WYSIWYG editor / 첨부파일 / 댓글 / FAQ admin / 카테고리 트리.
 *
 * Cycle 67: 본체 i18n 적용 (board-i18n.ts 의 17 → 75+ keys). cycle 57 의 BoardSidebar
 * 만 i18n 적용된 상태에서 본체의 한글 hardcoded 50개가 false negative 로 통과 (lint 가
 * `getSharedLang` import 만 보고 i18n 적용 page 로 판정). cycle 67 에서 본체 4 tab 전체 적용.
 */

interface SidebarMapping {
  domain: 'notice' | 'post' | 'inquiry' | 'faq';
  noticeImportantOnly?: boolean;
  postCategory?: PostCategory | '';
  inquiryCreating?: boolean;
  faqCategory?: string;
}

const SIDEBAR_TO_VIEW: Record<BoardSidebarItemId, SidebarMapping> = {
  notice_all: { domain: 'notice', noticeImportantOnly: false },
  notice_pinned: { domain: 'notice', noticeImportantOnly: true },
  board_all: { domain: 'post', postCategory: '' },
  board_free: { domain: 'post', postCategory: 'free' },
  board_tech: { domain: 'post', postCategory: 'tech' },
  board_info: { domain: 'post', postCategory: 'info' },
  inquiry_list: { domain: 'inquiry', inquiryCreating: false },
  inquiry_write: { domain: 'inquiry', inquiryCreating: true },
  faq_all: { domain: 'faq', faqCategory: undefined },
  faq_account: { domain: 'faq', faqCategory: '계정/보안' },
  faq_chat: { domain: 'faq', faqCategory: '채팅/알림' },
  faq_file: { domain: 'faq', faqCategory: '파일/연동' },
};

export function BoardPage() {
  useSyncExternalStore(subscribeBoardLang, getBoardLang, getBoardLang);
  const [activeSidebar, setActiveSidebar] = useState<BoardSidebarItemId>('notice_all');
  const [pinnedCount, setPinnedCount] = useState<number | undefined>(undefined);

  // 필독 공지 개수만 사이드바 badge 용으로 별도 fetch.
  useEffect(() => {
    boardApi
      .listNotices(true)
      .then((items) => setPinnedCount(items.length))
      .catch(() => setPinnedCount(undefined));
  }, []);

  const mapping = SIDEBAR_TO_VIEW[activeSidebar];

  return (
    <div className="board-page">
      <aside className="board-page__sidebar">
        <BoardSidebar
          activeId={activeSidebar}
          onSelect={setActiveSidebar}
          pinnedCount={pinnedCount}
        />
      </aside>

      <div className="board-page__content">
        {mapping.domain === 'notice' && (
          <NoticeTab key={activeSidebar} initialImportantOnly={mapping.noticeImportantOnly ?? false} />
        )}
        {mapping.domain === 'post' && (
          <PostTab key={activeSidebar} initialCategory={mapping.postCategory ?? ''} />
        )}
        {mapping.domain === 'inquiry' && (
          <InquiryTab key={activeSidebar} initialCreating={mapping.inquiryCreating ?? false} />
        )}
        {mapping.domain === 'faq' && (
          <FaqTab key={activeSidebar} initialCategory={mapping.faqCategory} />
        )}
      </div>
    </div>
  );
}

// ─── Notice Tab ─────────────────────────────────────────────

interface NoticeTabProps {
  initialImportantOnly?: boolean;
}

function NoticeTab({ initialImportantOnly = false }: NoticeTabProps) {
  useSyncExternalStore(subscribeBoardLang, getBoardLang, getBoardLang);
  const [notices, setNotices] = useState<NoticeListItem[]>([]);
  const [importantOnly, setImportantOnly] = useState(initialImportantOnly);
  const [selected, setSelected] = useState<NoticeDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Cycle 81 — importantOnly 토글 stale-response race 봉합 (FaqTab 동형, 코드베이스 표준 cancelled).
    let cancelled = false;
    boardApi.listNotices(importantOnly)
      .then((r) => { if (!cancelled) setNotices(r); })
      .catch((e) => { if (!cancelled) setError(tBoard('board.notice.err_load', { msg: e.message })); });
    return () => { cancelled = true; };
  }, [importantOnly]);

  const openNotice = useCallback(async (id: string) => {
    try {
      const detail = await boardApi.getNotice(id);
      setSelected(detail);
    } catch (e: any) {
      setError(tBoard('board.notice.err_detail', { msg: e.message }));
    }
  }, []);

  return (
    <section aria-labelledby="notice-h">
      <h2 id="notice-h">{importantOnly ? tBoard('board.notice.heading_pinned') : tBoard('board.notice.heading_all')}</h2>
      <div className="board-action-row">
        <label>
          <input type="checkbox" checked={importantOnly}
            onChange={(e) => { setImportantOnly(e.target.checked); setSelected(null); }} />
          {' '}{tBoard('board.notice.label_important_only')}
        </label>
      </div>
      <ul className="board-list">
        {notices.map((n) => (
          <li key={n.id} role="button" tabIndex={0}
            className={`board-list-item${n.isImportant ? ' important' : ''}`}
            onClick={() => openNotice(n.id)}>
            <span className="board-title">{n.title}</span>
            <span className="board-meta">{n.author.name} · {formatDate(n.postedAt)} {tBoard('board.suffix.view_count', { n: n.viewCount })}</span>
          </li>
        ))}
        {notices.length === 0 && <li className="board-meta">{tBoard('board.notice.empty')}</li>}
      </ul>
      {selected && (
        <div className="board-detail">
          <h3>{selected.title}</h3>
          <p className="board-meta">{selected.author.name} · {formatDateTime(selected.postedAt)}</p>
          <div>{selected.content}</div>
        </div>
      )}
      {error && <p className="board-error" role="alert">{error}</p>}
    </section>
  );
}

// ─── Post Tab ──────────────────────────────────────────────

interface PostTabProps {
  initialCategory?: PostCategory | '';
}

function PostTab({ initialCategory = '' }: PostTabProps) {
  useSyncExternalStore(subscribeBoardLang, getBoardLang, getBoardLang);
  const [posts, setPosts] = useState<PostListItem[]>([]);
  const [category, setCategory] = useState<PostCategory | ''>(initialCategory);
  const [selected, setSelected] = useState<PostDetail | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ title: '', content: '', category: 'free' as PostCategory });
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    boardApi.listPosts(category || undefined)
      .then(setPosts)
      .catch((e) => setError(tBoard('board.post.err_list', { msg: e.message })));
  }, [category]);

  useEffect(refresh, [refresh]);

  async function open(id: string) {
    try { setSelected(await boardApi.getPost(id)); }
    catch (e: any) { setError(tBoard('board.post.err_detail', { msg: e.message })); }
  }

  async function submit() {
    setError(null);
    if (!draft.title.trim() || !draft.content.trim()) {
      setError(tBoard('board.post.err_required')); return;
    }
    try {
      await boardApi.createPost({ title: draft.title.trim(), content: draft.content.trim(), category: draft.category });
      setDraft({ title: '', content: '', category: 'free' });
      setCreating(false);
      refresh();
    } catch (e: any) {
      setError(tBoard('board.post.err_create', { msg: e.message }));
    }
  }

  const heading =
    category === 'free' ? tBoard('board.post.heading_free') :
    category === 'tech' ? tBoard('board.post.heading_tech') :
    category === 'info' ? tBoard('board.post.heading_info') :
    tBoard('board.post.heading_all');

  const postFilterOptions = POST_FILTER_OPTION_KEYS.map((o) => ({ value: o.value, label: tBoard(o.labelKey) }));
  const postDraftOptions = POST_DRAFT_OPTION_KEYS.map((o) => ({ value: o.value, label: tBoard(o.labelKey) }));

  return (
    <section aria-labelledby="post-h">
      <h2 id="post-h">{heading}</h2>
      <div className="board-action-row">
        <span id="post-cat-label">{tBoard('board.post.label_category')}</span>
        <Combobox
          value={category}
          onChange={(v) => { setCategory(v as PostCategory | ''); setSelected(null); }}
          options={postFilterOptions}
          ariaLabel={tBoard('board.post.label_category')}
          className="board-select"
        />
        <button className="board-btn" onClick={() => setCreating(!creating)}>
          {creating ? tBoard('board.btn.cancel') : tBoard('board.btn.create_post')}
        </button>
      </div>
      {creating && (
        <div className="board-detail">
          <input className="board-input" style={{ width: '100%', marginBottom: 8 }}
            placeholder={tBoard('board.placeholder.title')} value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
          <textarea className="board-textarea" placeholder={tBoard('board.placeholder.content')}
            value={draft.content}
            onChange={(e) => setDraft({ ...draft, content: e.target.value })} />
          <div className="board-action-row">
            <Combobox
              value={draft.category}
              onChange={(v) => setDraft({ ...draft, category: v as PostCategory })}
              options={postDraftOptions}
              ariaLabel={tBoard('board.post.aria_draft_category')}
              className="board-select"
            />
            <button className="board-btn" onClick={submit}>{tBoard('board.btn.register')}</button>
          </div>
        </div>
      )}
      <ul className="board-list">
        {posts.map((p) => (
          <li key={p.id} role="button" tabIndex={0} className="board-list-item"
            onClick={() => open(p.id)}>
            <span className="board-title">[{p.category}] {p.title}</span>
            <span className="board-meta">{p.author.name} · {formatDate(p.createdAt)} {tBoard('board.suffix.view_count', { n: p.viewCount })}</span>
          </li>
        ))}
        {posts.length === 0 && <li className="board-meta">{tBoard('board.post.empty')}</li>}
      </ul>
      {selected && (
        <div className="board-detail">
          <h3>{selected.title}</h3>
          <p className="board-meta">{selected.author.name} · {formatDateTime(selected.createdAt)}</p>
          <div>{selected.content}</div>
        </div>
      )}
      {error && <p className="board-error" role="alert">{error}</p>}
    </section>
  );
}

// ─── FAQ Tab ───────────────────────────────────────────────

interface FaqTabProps {
  initialCategory?: string;
}

function FaqTab({ initialCategory }: FaqTabProps) {
  useSyncExternalStore(subscribeBoardLang, getBoardLang, getBoardLang);
  const [faqs, setFaqs] = useState<FaqDto[]>([]);
  const [q, setQ] = useState('');
  const [opened, setOpened] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Cycle 81 — search-as-you-type stale-response race 봉합. cleanup 이 타이머만 clearTimeout
    // 하면 이미 발사된 in-flight 요청은 추적 안 돼, 응답 순서 역전 시 옛 검색 결과가 최신을 덮어씀
    // (입력 box ↔ list 불일치). cancelled flag 로 dep 변경/unmount 후 도착한 응답을 무시 (코드베이스
    // 표준 패턴 — RecipientPicker/useDomainAvailability 등과 동형).
    let cancelled = false;
    const handle = setTimeout(() => {
      boardApi.listFaqs(initialCategory, q.trim() || undefined)
        .then((r) => { if (!cancelled) setFaqs(r); })
        .catch((e) => { if (!cancelled) setError(tBoard('board.faq.err_load', { msg: e.message })); });
    }, 300);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [q, initialCategory]);

  async function feedback(id: string, helpful: boolean) {
    try {
      const updated = await boardApi.postFaqFeedback(id, helpful);
      setFaqs((prev) => prev.map((f) => f.id === id ? updated : f));
    } catch (e: any) {
      setError(tBoard('board.faq.err_feedback', { msg: e.message }));
    }
  }

  function toggle(id: string) {
    setOpened((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  return (
    <section aria-labelledby="faq-h">
      <h2 id="faq-h">{initialCategory ? tBoard('board.faq.heading_category', { category: initialCategory }) : tBoard('board.faq.heading_all')}</h2>
      <div className="board-action-row">
        <input className="board-input" placeholder={tBoard('board.faq.search_placeholder')} value={q}
          onChange={(e) => setQ(e.target.value)} aria-label={tBoard('board.faq.search_aria')} />
      </div>
      {faqs.map((f) => {
        const isOpen = opened.has(f.id);
        return (
          <div key={f.id} className="board-faq-item">
            <div className="board-faq-q" onClick={() => toggle(f.id)} role="button" tabIndex={0}>
              [{f.category}] {f.question}
            </div>
            {isOpen && (
              <>
                <div className="board-faq-a">{f.answer}</div>
                <div className="board-faq-feedback">
                  <button className="board-btn-secondary" onClick={() => feedback(f.id, true)}>
                    👍 {f.helpfulCount}
                  </button>
                  <button className="board-btn-secondary" onClick={() => feedback(f.id, false)}>
                    👎 {f.notHelpfulCount}
                  </button>
                </div>
              </>
            )}
          </div>
        );
      })}
      {faqs.length === 0 && <p className="board-meta">{tBoard('board.faq.empty')}</p>}
      {error && <p className="board-error" role="alert">{error}</p>}
    </section>
  );
}

// ─── Inquiry Tab ───────────────────────────────────────────

interface InquiryTabProps {
  initialCreating?: boolean;
}

function InquiryTab({ initialCreating = false }: InquiryTabProps) {
  useSyncExternalStore(subscribeBoardLang, getBoardLang, getBoardLang);
  const [inquiries, setInquiries] = useState<InquiryDto[]>([]);
  const [creating, setCreating] = useState(initialCreating);
  const [draft, setDraft] = useState({ category: '계정', title: '', content: '' });
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    boardApi.listInquiries()
      .then(setInquiries)
      .catch((e) => setError(tBoard('board.inquiry.err_list', { msg: e.message })));
  }, []);
  useEffect(refresh, [refresh]);

  async function submit() {
    setError(null);
    if (!draft.title.trim() || !draft.content.trim() || !draft.category.trim()) {
      setError(tBoard('board.inquiry.err_required')); return;
    }
    try {
      await boardApi.createInquiry({
        category: draft.category.trim(),
        title: draft.title.trim(),
        content: draft.content.trim(),
      });
      setDraft({ category: '계정', title: '', content: '' });
      setCreating(false);
      refresh();
    } catch (e: any) {
      setError(tBoard('board.inquiry.err_create', { msg: e.message }));
    }
  }

  const inquiryCategoryOptions = INQUIRY_CATEGORY_OPTION_KEYS.map((o) => ({ value: o.value, label: tBoard(o.labelKey) }));

  return (
    <section aria-labelledby="inq-h">
      <h2 id="inq-h">{creating ? tBoard('board.inquiry.heading_create') : tBoard('board.inquiry.heading_list')}</h2>
      <div className="board-action-row">
        <button className="board-btn" onClick={() => setCreating(!creating)}>
          {creating ? tBoard('board.btn.cancel') : tBoard('board.btn.create_inquiry')}
        </button>
      </div>
      {creating && (
        <div className="board-detail">
          <Combobox
            value={draft.category}
            onChange={(v) => setDraft({ ...draft, category: v })}
            options={inquiryCategoryOptions}
            ariaLabel={tBoard('board.inquiry.aria_category')}
            className="board-select"
          />
          <input className="board-input" style={{ width: '100%', margin: '8px 0' }}
            placeholder={tBoard('board.placeholder.title')} value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
          <textarea className="board-textarea" placeholder={tBoard('board.placeholder.content')}
            value={draft.content}
            onChange={(e) => setDraft({ ...draft, content: e.target.value })} />
          <div className="board-action-row">
            <button className="board-btn" onClick={submit}>{tBoard('board.btn.register')}</button>
          </div>
        </div>
      )}
      <ul className="board-list">
        {inquiries.map((i) => (
          <li key={i.id} className="board-list-item">
            <span className="board-title">[{i.category}] {i.title}</span>
            <span className="board-meta">
              <span className={`board-status-pill board-status-${i.status.toLowerCase()}`}>
                {i.status === 'PENDING' ? tBoard('board.inquiry.status_pending') : tBoard('board.inquiry.status_answered')}
              </span>
              {' '}{formatDate(i.createdAt)}
            </span>
          </li>
        ))}
        {inquiries.length === 0 && <li className="board-meta">{tBoard('board.inquiry.empty')}</li>}
      </ul>
      {error && <p className="board-error" role="alert">{error}</p>}
    </section>
  );
}
